import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import NewsCard from '../components/NewsCard';
import ContactForm from '../components/ContactForm';
import { imageUrl } from '../lib/images';

const coreServices = [
  {
    title: 'Vejtransport',
    desc: 'Læs om dine muligheder med vejtransport',
    img: '/_assets/vtrucks.jpg',
    path: '/godstransport-i-europa',
  },
  {
    title: 'Multimodal transport',
    desc: 'Læs om dine muligheder med kombineret transport',
    img: '/_assets/fastlane-multi-indg.jpg',
    path: '/fragt-kombineret-transport',
  },
  {
    title: 'Express',
    desc: 'Læs om dine muligheder med express',
    img: '/_assets/fastlane-indg-kurer.jpg',
    path: '/ekspres-kurer-i-direkte-lastbiler',
  },
  {
    title: 'Fragtkontoret',
    desc: 'Kontakt os for kompetent rådgivning',
    img: 'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/arne-og-inger-4-web.jpg',
    path: '/fragtkontoret',
  },
  {
    title: 'Lager & Logistik',
    desc: 'Læs om vores lager',
    img: '/_assets/fastlane-indg-lager.jpg',
    path: '/lager',
  },
  {
    title: 'Søfragt',
    desc: 'Læs om dine muligheder med søfragt',
    img: '/_assets/fastlane-indg-soefragt.jpg',
    path: '/containerfragt-soefragt',
  },
  {
    title: 'Cross Trade',
    desc: 'Læs om fordelene ved cross trade',
    img: '/_assets/fastlane-indg-luftfragt.jpg',
    path: '/cross-trade',
  },
  {
    title: 'Fragtopgaver-referencer',
    desc: 'Se billeder af gods vi har kørt',
    img: '/_assets/fastlane-indg-gods.jpg',
    path: '/godstransport-referencer',
  },
];

export default function Home() {
  const [latestNews, setLatestNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      img: 'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/arne-og-inger-4-web.jpg',
      alt: 'Fastlane fragtkontoret - Arne og Inger',
    },
    {
      img: '/_assets/fragt-af-40-ft-hc-1.jpg',
      alt: 'Fragt af 40 ft High Cube container - Fastlane søfragt',
    },
    {
      img: '/_assets/fragt-af-landbrugsmaskiner.jpg',
      alt: 'Fragt af landbrugsmaskiner og specialgods',
    },
    {
      img: '/_assets/137y0293-2-0.jpg',
      alt: 'Skib med containere og løstrailere shortsea - Fastlane shipping',
    },
  ];

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? heroSlides.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    async function loadNews() {
      try {
        const res = await fetch('/_api/public/content/nyheder');
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : data?.items || [];
          const published = items.filter((n) => n.publiceret !== false);
          // sort descending by date
          published.sort((a, b) => new Date(b.dato || 0) - new Date(a.dato || 0));
          setLatestNews(published.slice(0, 3));
        }
      } catch (err) {
        console.warn('Could not load latest news', err);
      } finally {
        setNewsLoading(false);
      }
    }
    loadNews();
  }, []);

  return (
    <>
      <SEO
        title="Shipping, Fragt og Spedition i hele verden - Få et tilbud i dag"
        description="Fastlane tilbyder international shipping af gods. Engageret og professionel outsourcing af alle former for transport og spedition."
        keywords="international, shipping, spedition, fragt, godstransport, transport, containerfragt, industrigods, lagerhotel, logistik, kurer, søfragt, luftfragt, landevejstransport, multimodaltransport, fragtpriser, containerfragt, fragtrater"
        canonicalUrl="https://fastlane.dk/"
      />

      {/* Full-width split hero section (approx 40/60 split) */}
      <section className="relative w-full bg-[#1a2d48] text-white overflow-hidden border-b border-slate-700/60">
        {/* Left Side Background: Dark solid background with subtle blueprint grid / line illustration */}
        <div className="absolute inset-y-0 left-0 w-full lg:w-[42%] bg-[#1a2d48] z-0 pointer-events-none">
          {/* Blueprint-style technical grid pattern */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.07] text-sky-200"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <pattern id="blueprint-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#blueprint-grid)" />
          </svg>

          {/* Understated blueprint-style line illustration / geometric curves */}
          <div className="absolute -bottom-20 -left-20 w-[480px] h-[480px] rounded-full border border-sky-400/10 pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-[360px] h-[360px] rounded-full border border-dashed border-sky-400/15 pointer-events-none" />
          <div className="absolute top-12 left-1/2 w-72 h-72 rounded-full border border-sky-300/10 pointer-events-none" />
        </div>

        {/* Right Side Image: Full-bleed hero image container (covers right 58% on desktop, stacked on mobile) */}
        <div className="relative w-full h-72 sm:h-96 lg:h-auto lg:absolute lg:inset-y-0 lg:right-0 lg:w-[58%] z-0 overflow-hidden">
          {heroSlides.map((slide, idx) => (
            <div
              key={slide.img}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                idx === currentSlide ? 'opacity-100 z-1' : 'opacity-0 z-0 pointer-events-none'
              }`}
            >
              <img
                src={imageUrl(slide.img, 'width=1600,quality=85,format=auto')}
                alt={slide.alt}
                className="w-full h-full object-cover object-center"
                loading={idx === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}

          {/* Soft dark overlay / gradient near the split for smooth transition and overlapping text readability */}
          <div
            className="hidden lg:block absolute inset-y-0 left-0 w-36 xl:w-52 bg-gradient-to-r from-[#1a2d48] via-[#1a2d48]/85 to-transparent z-10 pointer-events-none"
            aria-hidden="true"
          />
          {/* Mobile bottom-to-top gradient for clean text stacking */}
          <div
            className="lg:hidden absolute inset-0 bg-gradient-to-t from-[#1a2d48] via-[#1a2d48]/50 to-transparent z-10 pointer-events-none"
            aria-hidden="true"
          />
        </div>

        {/* Text Content Area: Aligns with standard horizontal page margin */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="min-h-[500px] sm:min-h-[560px] lg:min-h-[640px] xl:min-h-[680px] flex items-center py-12 sm:py-16 lg:py-20">
            {/* Left Content Column - extends across the 42% split boundary into image area */}
            <div className="w-full lg:w-[50%] xl:w-[48%] relative z-20">
              {/* Eyebrow Label (pill-style) */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-500/15 border border-sky-400/30 text-sky-200 text-xs font-semibold tracking-wider uppercase mb-5 backdrop-blur-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                <span>Global Spedition & Rådgivning</span>
              </div>

              {/* Bold Display Heading */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-extrabold text-white tracking-tight leading-[1.08] mb-5">
                International Shipping
              </h1>

              {/* Body Text */}
              <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-normal mb-8 max-w-xl">
                Vi sender alle almindelige typer gods i det meste af verden med lastbil, tog, skib eller fly. Primært i standard transportudstyr og via standard transportsystemer. Vi er et videncenter, der gerne rådgiver om alle forhold relateret til international fragt.
              </p>

              {/* Two CTA Buttons (Stacked on mobile, horizontal on tablet/desktop) */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                <Link
                  to="/fragtrater"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-lg bg-[#b25d19] hover:bg-[#974d12] text-white text-sm sm:text-base font-bold shadow-lg hover:shadow-xl transition-all active:scale-98"
                >
                  <span>Få et fragttilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/25 text-white text-sm sm:text-base font-semibold transition-all active:scale-98 backdrop-blur-xs"
                >
                  <span>Kontakt fragtkontoret</span>
                  <i className="fa-solid fa-phone text-xs text-sky-300" aria-hidden="true" />
                </Link>
              </div>

              {/* Thin horizontal divider line */}
              <div className="border-t border-slate-700/80 my-7 max-w-lg" />

              {/* Two small trust items with icons */}
              <div className="flex flex-col sm:flex-row gap-5 sm:gap-8 pt-1">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sky-400 shrink-0 border border-white/10">
                    <i className="fa-solid fa-shield-halved text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white uppercase tracking-wider">NSAB 2015 Betingelser</div>
                    <div className="text-[11px] text-slate-300">Professionel sikkerhed & rådgivning</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-emerald-400 shrink-0 border border-white/10">
                    <i className="fa-solid fa-earth-americas text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white uppercase tracking-wider">Verdensomspændende</div>
                    <div className="text-[11px] text-slate-300">Vejtransport, søfragt & luftfragt</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Navigation Control: fixed to bottom edge, exactly at the 42% split on desktop, hidden on mobile */}
        <div className="hidden lg:flex absolute bottom-0 left-[42%] -translate-x-1/2 z-30 bg-[#122033] border-t border-r border-slate-700/80 shadow-2xl divide-x divide-slate-700/80">
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Forrige billede"
            className="px-4 py-3.5 text-slate-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Næste billede"
            className="px-4 py-3.5 text-slate-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Core Service Areas - Industrial Logistics Grid (Reference Design) */}
      <section className="bg-[#edf0f5] py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-y-4 border-[#16325c]" aria-labelledby="core-services-heading">
        <div className="max-w-7xl mx-auto">
          {/* 4-Column Responsive Grid (4 boxes in each row on desktop) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {coreServices.map((service, idx) => (
              <Link
                key={idx}
                to={service.path}
                className="group flex flex-col bg-white border border-gray-300 rounded-xl overflow-hidden shadow-none hover:border-[#234d7d] hover:shadow-md transition-all cursor-pointer h-full"
              >
                {/* Visual Header: Image or Vector Illustration - made higher */}
                {service.illustration ? (
                  <div className="relative h-52 sm:h-56 w-full bg-[#89b1fb] flex items-center justify-center overflow-hidden shrink-0">
                    <i className={`text-5xl text-[#16325c] ${service.icon || 'fa-solid fa-truck-fast'}`} aria-hidden="true" />
                  </div>
                ) : (
                  <div className="relative h-52 sm:h-56 w-full overflow-hidden bg-gray-200 shrink-0">
                    <img
                      src={imageUrl(service.img, 'width=600,quality=85,format=auto')}
                      alt={service.title}
                      className="h-full w-full object-cover group-hover:scale-102 transition-transform duration-300"
                      loading={idx < 4 ? 'eager' : 'lazy'}
                    />
                  </div>
                )}

                {/* Content Area - expanded height */}
                <div className="flex flex-col items-center justify-between p-6 sm:py-8 sm:px-5 grow text-center min-h-[155px]">
                  <div className="w-full flex flex-col items-center">
                    <h3 className="text-xl font-normal tracking-tight text-gray-700 group-hover:text-[#16325c] transition-colors">
                      {service.title}
                    </h3>
                    {/* Centered Decorative Underline Divider */}
                    <div className="w-16 h-0.5 bg-[#234d7d] mt-2 mb-3" aria-hidden="true" />
                  </div>
                  <span className="text-xs text-gray-500 group-hover:text-[#16325c] transition-colors">
                    {service.desc}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic News Section (Seneste nyheder fra CMS) */}
      <section className="py-16 sm:py-20 bg-slate-50 border-t border-b border-slate-200" aria-labelledby="latest-news-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-xs font-bold text-[#c52828] uppercase tracking-widest">
                Fastlane Nyheder
              </span>
              <h2 id="latest-news-heading" className="text-3xl font-extrabold text-[#0f2b48] tracking-tight mt-1">
                Seneste nyheder
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Hold dig opdateret med nyheder, logistikprojekter og information fra Fastlane ApS.
              </p>
            </div>
            <Link
              to="/nyheder"
              className="inline-flex items-center text-sm font-bold text-[#0f2b48] hover:text-sky-700 transition-colors shrink-0"
            >
              <span>Se alle nyheder</span>
              <i className="fa-solid fa-arrow-right text-xs ml-2" aria-hidden="true" />
            </Link>
          </div>

          {newsLoading ? (
            <div className="py-12 text-center text-slate-400">
              <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
              Henter seneste nyheder fra CMS...
            </div>
          ) : latestNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {latestNews.map((newsItem) => (
                <NewsCard key={newsItem.id} item={newsItem} />
              ))}
            </div>
          ) : (
            <div className="bg-white p-8 rounded-xl text-center border border-slate-200 text-slate-600">
              Der er endnu ikke oprettet nyheder.
            </div>
          )}
        </div>
      </section>

      {/* Outsourcing & Videncenter Highlights */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold text-[#c52828] uppercase tracking-widest">
                Outsourcing af transport
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f2b48] tracking-tight">
                En samlet leverandør til alle typer godstransport
              </h2>
              <p className="text-slate-600 text-base leading-relaxed">
                Hos de fleste fragtudbydere skal du i dag bestille fragten online via en portal. Hos os bestiller du blot med en mail indeholdende de rigtige informationer, så sørger vi for resten.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">1</div>
                  <p className="text-sm text-slate-700"><strong>Fokusér på jeres kerneforretning:</strong> Slip for at holde viden om fragtportaler og regler opdateret.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">2</div>
                  <p className="text-sm text-slate-700"><strong>Personlig opfølgning:</strong> Vi lægger først en sending væk, når den er bekræftet leveret.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">3</div>
                  <p className="text-sm text-slate-700"><strong>Bedre fragtpriser:</strong> Vi udnytter samlede volumener og finder det bedste match i vores leverandørdatabase.</p>
                </div>
              </div>
              <div className="pt-2">
                <Link
                  to="/outsourcing"
                  className="inline-flex items-center text-sky-700 hover:text-sky-900 font-bold text-sm"
                >
                  <span>Læs alle 5 grunde til at outsource transport</span>
                  <i className="fa-solid fa-arrow-right text-xs ml-2" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <ContactForm
                title="Få en hurtig afklaring på din transport"
                subtitle="Send en besked til vores fragtkontor i Esbjerg – vi svarer hurtigt."
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
